import { Link } from "react-router-dom";
import { ShieldCheck, Radar, FileCheck2, Radio, ArrowRight, Phone, Mail, Shield } from "lucide-react";
import { services } from "../data/services";
import { citiesByProvince } from "../data/cities";
import { FR_SERVICE_SLUGS, FR_SLUGS, frServiceNames, frServiceShorts, frCityName, frHomeFaqs } from "../data/frContent";
import { getHead, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";
import { Seo } from "../components/Seo";
import { SectionHead, FAQBlock, CTABand } from "../components/Blocks";
import { IconTile, SERVICE_ICONS } from "../components/Icons";

const stats = [
  { value: "13", label: "Provinces et territoires" },
  { value: "690+", label: "Villes et communautés" },
  { value: "20", label: "Services spécialisés" },
  { value: "24/7", label: "Répartition et intervention" },
];

const why = [
  { icon: ShieldCheck, title: "Licenciés dans chaque province", text: "Chaque gardien détient un permis provincial ou territorial valide — BSP au Québec, PSISA en Ontario, Security Services Act en Colombie-Britannique — vérifié avant son premier quart." },
  { icon: Radar, title: "Patrouilles et rapports vérifiés", text: "Points de contrôle géolocalisés, audits de supervision et rapports d'incident horodatés : une preuve documentée de la couverture après chaque quart." },
  { icon: Radio, title: "Répartition en direct 24/7", text: "Une seule ligne, répondue jour et nuit. Couvertures urgentes — surveillance incendie, réponse aux alarmes — coordonnées dès votre appel." },
  { icon: FileCheck2, title: "Un partenaire imputable", text: "Visites des lieux, consignes écrites et une structure de commandement unique d'un océan à l'autre. Pas de sous-traitance en cascade, pas d'excuses." },
];

const head = getHead("/fr");

export default function FrHome() {
  const qcMajor = ["montreal", "laval", "quebec-city", "longueuil", "gatineau", "sherbrooke", "saguenay", "trois-rivieres", "terrebonne", "saint-jean-sur-richelieu", "levis", "brossard"];
  return (
    <>
      <Seo title={head.title} description={head.description} path="/fr" lang="fr-CA" alternates={head.alternates} />
      <section className="relative overflow-hidden border-b border-white/10" data-testid="fr-home-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
          <Shield className="absolute -right-16 top-1/2 -translate-y-1/2 h-[420px] w-[420px] text-[#FF5500] opacity-[0.07]" strokeWidth={0.5} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <div className="inline-flex items-center gap-2 border border-[#FF5500]/40 bg-[#FF5500]/10 px-3 py-1.5 mb-6 animate-fade-up" data-testid="fr-hero-dispatch-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#FF8A50]">Répartition 24/7 active · D'un océan à l'autre</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-none text-white max-w-4xl animate-fade-up" style={{ animationDelay: "80ms" }}>
            Gardiens de sécurité licenciés <span className="text-[#FF5500]">partout au Canada</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl animate-fade-up" style={{ animationDelay: "160ms" }}>
            Déploiement rapide de gardiens certifiés et en uniforme pour les environnements commerciaux, industriels, de construction et à haut risque — dans chaque province, territoire et grande ville canadienne.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <a href={PHONE_TEL} data-testid="fr-hero-phone-button" className="inline-flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors duration-200">
              <Phone className="h-4 w-4" /> Répartition : {PHONE}
            </a>
            <a href={EMAIL_MAILTO} data-testid="fr-hero-email-button" className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-medium px-7 py-3.5 rounded-sm transition-colors duration-200">
              <Mail className="h-4 w-4 text-[#FF5500]" /> {EMAIL}
            </a>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#131620]" data-testid="fr-stats-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center md:text-left" data-testid={`fr-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="font-display text-4xl font-extrabold text-[#FF5500]">{s.value}</p>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="fr-home-services">
        <SectionHead
          overline="Ce que nous faisons"
          title="20 services de sécurité spécialisés"
          sub="Des gardiens en uniforme à la télésurveillance, chaque service est offert par du personnel licencié au provincial avec une couverture documentée et vérifiable."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/fr/services/${FR_SERVICE_SLUGS[s.slug]}`}
              data-testid={`fr-home-service-${s.slug}`}
              className="group border border-white/10 bg-[#181C28] rounded-sm overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-[#FF5500]/50 hover:shadow-[0_10px_30px_rgba(255,85,0,0.15)]"
            >
              <IconTile icon={SERVICE_ICONS[s.slug] || Shield} className="h-36 border-b border-white/10" />
              <div className="p-5">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight text-white group-hover:text-[#FF5500] transition-colors">{frServiceNames[s.slug]}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{frServiceShorts[s.slug]}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#FF5500]">
                  Détails <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#131620] border-y border-white/10" data-testid="fr-why-maze">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHead
            overline="Pourquoi Maze"
            title="Une sécurité que vous pouvez vérifier"
            sub="N'importe quelle entreprise peut promettre une protection. Maze Security Solutions la prouve — par les permis, la supervision et la documentation."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {why.map((w) => (
              <div key={w.title} className="border border-white/10 bg-[#0D0E12] p-6 rounded-sm" data-testid={`fr-why-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <w.icon className="h-8 w-8 text-[#FF5500] mb-4" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="fr-home-quebec">
        <SectionHead
          overline="Au Québec"
          title="Villes du Québec desservies"
          sub="Des gardiens licenciés par le Bureau de la sécurité privée dans 99 villes du Québec — avec des pages entièrement en français pour chacune."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {qcMajor.map((slug) => (
            <Link
              key={slug}
              to={`/fr/${FR_SLUGS[slug]}`}
              data-testid={`fr-home-city-${slug}`}
              className="group border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm px-4 py-3 transition-all duration-200 hover:-translate-y-0.5"
            >
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors flex items-center justify-between gap-1">
                {frCityName(slug)}
                <ArrowRight className="h-3.5 w-3.5 text-[#FF5500] opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/fr/villes" data-testid="fr-all-cities-link" className="inline-flex items-center gap-1.5 text-sm text-[#FF5500] hover:text-white transition-colors font-medium">
            Voir les 99 villes du Québec <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <FAQBlock faqs={frHomeFaqs} heading="Questions fréquentes" />
      <CTABand title="Besoin de gardiens sur place?" sub="Un appel rejoint notre répartition 24/7. Décrivez la propriété, les risques et l'échéancier — nous nous occupons du reste." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-xs text-gray-500 leading-relaxed max-w-4xl">
          Maze Security Solutions offre des services de gardiens de sécurité licenciés, patrouille mobile, sécurité de chantiers, sécurité d'événements, concierge, prévention des pertes, surveillance incendie, réponse aux alarmes et télésurveillance partout au Canada — y compris Montréal, Laval, Québec, Toronto, Vancouver, Calgary, Edmonton, Ottawa, Winnipeg, Halifax et des centaines d'autres villes.
        </p>
      </div>
    </>
  );
}
